import {
  getGap,
  getPaddings,
  mergeCtaSettings,
  mergeTypographySettings,
} from "@/utils/theme-helper";
import CtaComponent from "./CtaComponent";
import TypographyComponent, {
  TypographyComponentProps,
} from "./TypographyComponent";
import { CMSData, CMSSection, CtaComponentProps } from "@/cms/models";
import ContactForm, { ContactFormProps } from "./forms/ContactForm";
import { ApplicationFraction } from "@/utils/gql/graphql";
import { storeText } from "@/utils/store-content";
import ImageComponent from "./ImageComponent";
import { selectablePreview, type JsonPath } from "@/cms-preview/select";
import { elementUnit, type MotionScope } from "@/utils/motion";

export interface StackedContentComponentProps {
  gap: "xs" | "sm" | "md" | "lg" | "xl";
  items: any[];
  padding: string;
  cmsData: CMSData;
  justify: "start" | "center" | "end";
}

export const ContentComponents: { [key: string]: React.FC<any> } = {
  CtaComponent: CtaComponent,
  TypographyComponent: TypographyComponent,
  ImageComponent: ImageComponent,
  ContactForm: ContactForm,
};

export const GetContentComponent = (
  cmsData: CMSData,
  item: any,
  key: string,
  index: number,
  app: ApplicationFraction,
  recaptchaSiteKey: string,
  path?: JsonPath,
  section?: MotionScope,
) => {
  const Component = ContentComponents[item.type];
  const config = loadConfig(item, cmsData, app);
  const unit = elementUnit(cmsData?.theme, section, item, index);
  return (
    <div
      key={key}
      className={`flex w-full justify-${item.justify ?? "center"}`}
      data-re-path={
        path && selectablePreview(cmsData) ? JSON.stringify(path) : undefined
      }
      {...unit?.attributes}
      style={unit?.style}
    >
      <Component
        id={key}
        itemIndex={index}
        config={config}
        recaptchaSiteKey={recaptchaSiteKey}
      />
    </div>
  );
};

export const loadConfig = (
  item: {
    type: string;
    config: TypographyComponentProps | CtaComponentProps | ContactFormProps;
  },
  cmsData: CMSData,
  app: ApplicationFraction,
): TypographyComponentProps | CtaComponentProps | any => {
  switch (item.type) {
    case "CtaComponent":
      return mergeCtaSettings(item.config as CtaComponentProps, cmsData, {
        size: "sm",
      });
    case "ContactForm":
      const foo = { app: app, cmsData: cmsData };
      console.log("ContactForm", foo);
      return foo;
    default: {
      const config = mergeTypographySettings(item.config, cmsData);
      return item.type === "TypographyComponent" &&
        typeof config.text === "string" &&
        config.text.includes("data-reweb-store-field")
        ? { ...config, text: storeText(config.text, app.resourceGroup) }
        : config;
    }
  }
};

const StackedContentComponent: React.FC<{
  config: StackedContentComponentProps;
  index: string;
  cmsData: CMSData;
  app: ApplicationFraction;
  cmsSection: CMSSection<any>;
  list: "content" | "leftContent" | "rightContent";
  // Every section rendering this is a Client Component, where
  // process.env.RECAPTCHA_SITE_KEY is undefined, so SectionProvider hands it down.
  recaptchaSiteKey: string;
}> = ({ config, index, cmsData, cmsSection, app, recaptchaSiteKey, list }) => {
  return (
    <div
      style={{ backgroundColor: cmsSection.specifics.content?.bgColor }}
      className={`flex h-full flex-col w-full items-${cmsSection.specifics.items ?? "center"} justify-${cmsSection.specifics.justify ?? "center"} ${getPaddings(config.padding)} ${getGap(config.gap)}`}
    >
      {config.items.map((item, itemIndex) =>
        GetContentComponent(
          cmsData,
          item,
          `stacked-content-${index}-${itemIndex}`,
          itemIndex,
          app,
          recaptchaSiteKey,
          ["specifics", list, "items", itemIndex],
          { motion: cmsSection.ui?.motion, first: Number(index) === 0 },
        ),
      )}
    </div>
  );
};

export default StackedContentComponent;
